"use client";

import { useTagPolicy, tagExcluded } from "@/lib/finance/finance-data-context";
import { Segmented } from "@/components/finance/ui";
import { MenuCheckRow, MenuPanel, MenuTrigger, useDismissableMenu } from "@/components/finance/menu";

/**
 * Tag control. Two independent capabilities:
 *   • Filter ("Show only") — narrow the visible transactions to rows carrying a
 *     selected tag (OR across selected). Page-local; rendered only when the
 *     host passes filter props (the Transactions ledger). Hovering a tag
 *     reveals "Only" when the host can set the whole filter at once.
 *   • Budgeting (In/Out) — whether a tag's transactions count toward
 *     spending/income everywhere (global policy; default matches Empower:
 *     reimbursable out, the rest in).
 * Renders nothing when the data carries no tags.
 */
export function TagMenu({
  filterTags,
  onToggleFilter,
  onSetFilter,
  sizeClass,
}: {
  /** Currently-selected filter tags (from the page's URL state). */
  filterTags?: string[];
  /** Toggle one tag in the filter. Presence of this prop enables the filter section. */
  onToggleFilter?: (tag: string) => void;
  /** Replace the whole filter — enables the per-row "Only" button. */
  onSetFilter?: (tags: string[]) => void;
  /** Trigger padding + text size override (default: the toolbar pill size). */
  sizeClass?: string;
}) {
  const { policy, setTag, tags } = useTagPolicy();
  const menu = useDismissableMenu();
  const filterable = !!onToggleFilter;
  const selected = new Set(filterTags ?? []);

  if (!tags.length) return null;
  const excludedCount = tags.filter((t) => tagExcluded(t, policy)).length;
  const filterCount = filterable ? selected.size : 0;

  return (
    <div ref={menu.ref} className="relative">
      <MenuTrigger open={menu.open} onClick={menu.toggle} sizeClass={sizeClass}>
        Tags
        {filterCount > 0 && (
          <span className="rounded-full bg-accent/15 px-1.5 py-0.5 text-[11px] font-medium leading-none text-accent-ink">
            {filterCount} filtered
          </span>
        )}
        {excludedCount > 0 && (
          <span className="rounded-full bg-warn/15 px-1.5 py-0.5 text-[11px] font-medium leading-none text-warn">
            {excludedCount} hidden
          </span>
        )}
      </MenuTrigger>

      {menu.open && (
        <MenuPanel className="p-1.5">
          {filterable && (
            <div className="mb-1 border-b border-edge pb-1.5">
              <div className="flex items-center justify-between px-2 pb-1 pt-1">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-3">Show only</span>
                {filterCount > 0 && (
                  <button
                    onClick={() => (onSetFilter ? onSetFilter([]) : selected.forEach((t) => onToggleFilter!(t)))}
                    className="text-[11px] font-medium text-accent-ink hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              {tags.map((tag) => (
                <MenuCheckRow
                  key={tag}
                  label={tag}
                  checked={selected.has(tag)}
                  onToggle={() => onToggleFilter!(tag)}
                  onOnly={onSetFilter ? () => onSetFilter([tag]) : undefined}
                  dataKey={`tag:${tag}`}
                />
              ))}
            </div>
          )}

          <p className="px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wide text-ink-3">Budgeting</p>
          {tags.map((tag) => {
            const excluded = tagExcluded(tag, policy);
            return (
              <div key={tag} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-3/50">
                <span className="min-w-0 truncate text-sm">{tag}</span>
                <Segmented
                  ariaLabel={`${tag} budgeting`}
                  options={[
                    { value: "in", label: "In" },
                    { value: "out", label: "Out" },
                  ]}
                  value={excluded ? "out" : "in"}
                  onChange={(v) => setTag(tag, v === "in")}
                />
              </div>
            );
          })}
        </MenuPanel>
      )}
    </div>
  );
}
